import {
  Button,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Typography,
  Box,
  Grid,
  Paper,
  useTheme,
  Checkbox,
  Snackbar,
  Alert,
  InputAdornment,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { PaymentClient } from "../../utilities/backend/payment";
import { useSelector } from "react-redux";
import { RootState } from "../../utilities/redux/store";
import { UsersClient } from "../../utilities/backend/client";
import { AuthClient } from "../../utilities/backend/client";
import CampaignCardDetail from "../../components/CampaignCardDetail";
import { roundOffAmount } from "../../utilities/helpers/currency";

const DEFAULT_PHONE_NUMBER = "1111111111";
const DEFAULT_PASSWORD = "Secur2025password";

interface FormErrors {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

const InvestmentPayment = () => {
  const authClient = new AuthClient();

  const [loading, setLoading] = useState<boolean>(false);
  const [paymentMethod, setPaymentMethod] = useState("DAF");
  const [checked, setChecked] = useState(false);
  const location = useLocation();
  const {
    investmentAmount: amount,
    returnUrl,
    recommendationData,
    isAnonymous,
    defaultValues,
    minimalInvestment,
    accountBalance
  } = location.state || {};
  const authToken = useSelector((state: RootState) => state.token);

  const isAuthenticated = authToken.token !== null;

  const [notificationError, setNotificationError] = useState<{
    status: boolean;
    text?: string;
  }>({ status: false });
  const [investmentError, setInvestmentError] = useState("");

  const userClient = new UsersClient();

  useEffect(() => {
    setInvestmentError("")
  }, [amount])

  const defaultFormData = useMemo(() => {
    if (recommendationData) {
      return {
        firstName: recommendationData.firstName,
        lastName: recommendationData.lastName,
        email: recommendationData.userEmail,
      };
    }
    if (defaultValues) {
      return {
        firstName: defaultValues.firstName,
        lastName: defaultValues.lastName,
        email: defaultValues.email,
      };
    }
    return {
      firstName: null,
      lastName: null,
      email: null,
    };
  }, [recommendationData, defaultValues]);

  const [formData, setFormData] = useState(defaultFormData);

  const [formErrors, setFormErrors] = useState<FormErrors>({
    firstName: null,
    lastName: null,
    email: null,
  });

  const [investmentAmount, setInvestmentAmount] = useState<string>(amount ? roundOffAmount(amount).toString() : "250");

  const theme = useTheme();
  const navigate = useNavigate();

  const paymentClient = new PaymentClient();

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    if (value.length > 1) {
      value = value.replace(/^0+/, "");
    }
    if (/^\d*$/.test(value)) {
      setInvestmentAmount(value);
      if(value === "" && minimalInvestment || minimalInvestment && parseInt(value) < minimalInvestment - (accountBalance ? accountBalance : 0))
        setInvestmentError(`Minimum investment amount is ${minimalInvestment} ${accountBalance ? `, you currently have $${accountBalance} in your account` : ""}`);
      else {
        setInvestmentError("");
      };
    }
  };

  const validateForm = async () => {
    let errors: any = {};
    let isValid = true;

    if (!formData.email) {
      errors.email = "Email is required";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
      isValid = false;
    } else if (
      isAnonymous &&
      paymentMethod !== "DAF" &&
      paymentMethod !== "Foundation"
    ) {
      try {
        const response = await paymentClient.validateDuplicateEmail(
          formData.email
        );
        if (!response.success) {
          errors.email =
            "Your email is already registered, please log in or reset your password.";
          isValid = false;
        }
      } catch (error) {
        console.error("Error checking email:", error);
        errors.email = "Error validating email. Please try again.";
        isValid = false;
      }
    }

    if (!formData.firstName) {
      errors.firstName = "First name is required";
      isValid = false;
    }

    if (!formData.lastName) {
      errors.lastName = "Last name is required";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handlePaymentChange = (event: any) => {
    setPaymentMethod(event.target.value);
  };

  const handleInputChange = (event: any) => {
    const { name, value } = event.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "email") {
      if (/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) {
        setFormErrors((prev) => ({ ...prev, email: null }));
      } else {
        setFormErrors((prev) => ({
          ...prev,
          email: "Please enter a valid email address",
        }));
      }
    }

    if (name === "firstName") {
      if (value.trim() === "") {
        setFormErrors((prev) => ({
          ...prev,
          firstName: "First name is required",
        }));
      } else if (/[^a-zA-Z0-9_]/.test(value)) {
        setFormErrors((prev) => ({
          ...prev,
          firstName:
            "First name should contain only letters, numbers, or underscores.",
        }));
      } else {
        setFormErrors((prev) => ({ ...prev, firstName: null }));
      }
    }

    if (name === "lastName") {
      if (value.trim() === "") {
        setFormErrors((prev) => ({
          ...prev,
          lastName: "Last name is required",
        }));
      } else if (/[^a-zA-Z0-9_]/.test(value)) {
        setFormErrors((prev) => ({
          ...prev,
          lastName:
            "Last name should contain only letters, numbers, or underscores.",
        }));
      } else {
        setFormErrors((prev) => ({ ...prev, lastName: null }));
      }
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const validated = await validateForm();
      if (!validated) {
        setLoading(false);
        return;
      }

      const campaignId = recommendationData?.campaign?.id;
      const isInvestmentPage = returnUrl?.startsWith("/invest");
      const isDAFOrFoundation =
        paymentMethod === "DAF" || paymentMethod === "Foundation";

      if (isAnonymous && isInvestmentPage) {
        updateStoredRecommendationData();

        if (isDAFOrFoundation && amount > 10) {
          const registrationSuccessful = await registerAnonymousUser();
          if (!registrationSuccessful) {
            setLoading(false);
            return;
          }
        }
      }

      navigate(getNavigationPath(), {
        replace: true,
        state: buildNavigationState(campaignId),
      });
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      setLoading(false);
    }
  };

  const updateStoredRecommendationData = () => {
    try {
      const storedData = localStorage.getItem("recommendationData");
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        const updatedData = {
          ...parsedData,
          userFullName: `${formData.firstName} ${formData.lastName}`,
          userEmail: formData.email,
        };
        localStorage.setItem("recommendationData", JSON.stringify(updatedData));
      }
    } catch (error) {
      console.error("Error parsing recommendationData:", error);
    }
  };

  const registerAnonymousUser = async () => {
    try {
      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        userName: `${formData.firstName}${formData.lastName}`,
        password: DEFAULT_PASSWORD,
        email: formData.email,
        phoneNumber: DEFAULT_PHONE_NUMBER,
      };

      const response = await authClient.auth_RegisterUser(userData);

      if (!response.success) {
        const newFormErrors: FormErrors = {
          firstName: null,
          lastName: null,
          email: null,
        };

        let hasFormRelatedError = false;
        let notificationMessage = "";

        response.errors.forEach((error) => {
          switch (error.code) {
            case "DuplicateUserName":
              newFormErrors.firstName = error.description;
              hasFormRelatedError = true;
              break;
            case "DuplicateEmail":
              newFormErrors.email = error.description;
              hasFormRelatedError = true;
              break;
            default:
              notificationMessage = error.description || "Registration failed.";
              break;
          }
        });

        if (hasFormRelatedError) {
          setFormErrors(newFormErrors);
        }

        if (!hasFormRelatedError && notificationMessage) {
          setNotificationError({
            status: true,
            text: notificationMessage,
          });
        }

        return false;
      }

      setFormErrors({
        firstName: null,
        lastName: null,
        email: null,
      });

      return true;
    } catch (error) {
      console.error("Error registering anonymous user:", error);
      setNotificationError({
        status: true,
        text: "An error occurred while registering.",
      });
      return false;
    }
  };

  const buildNavigationState = (campaignId: string = "") => ({
    email: formData.email,
    firstName: formData.firstName,
    lastName: formData.lastName,
    investmentAmount,
    campaignId,
    ...(returnUrl ? { returnUrl } : {}),
    ...(isAnonymous ? { isAnonymous } : {}),
  });

  const getNavigationPath = () => {
    switch (paymentMethod) {
      case "DAF":
        return "/daf-donation";
      case "ACH":
        return "/ach-payment";
      case "Foundation":
        return "/foundation-payment";
      case "CreditCard":
        return "/fund-investment";
      default:
        return "/";
    }
  };

  useEffect(() => {
    if (authToken.token) {
      userClient
        .users_GetUser({ token: authToken.token })
        .then(({ email, firstName, lastName }) => {
          setFormData((prevData) => ({
            ...prevData,
            firstName: firstName || prevData.firstName,
            lastName: lastName || prevData.lastName,
            email: email || prevData.email,
          }));
        })
        .catch((error) => {
          console.error("Failed to fetch user data:", error);
        });
    }
  }, [authToken.token]);

  useEffect(() => {
    if (amount) {
      setInvestmentAmount(roundOffAmount(amount).toString())
    }
  }, [amount])

  const showTerms = Boolean(!isAuthenticated);

  const disableSubmit = (showTerms && !checked) || loading || Boolean(formErrors.email) || Boolean(formErrors.firstName) || Boolean(formErrors.lastName) || investmentAmount === "" || investmentAmount === "0" || investmentError != "";

  return (
    <>
      <Grid container justifyContent="center">
        <Grid item xs={12} sm={8} md={8} lg={8} pt={5}>
          <Box sx={{ padding: "20px" }}>
            {recommendationData?.campaign && (
              <>
                <Typography
                  sx={{
                    fontSize: 44,
                    fontFamily: "Poppins",
                    lineHeight: 1.23,
                    fontWeight: 600,
                    color: "#20527F",
                    [theme.breakpoints.down("sm")]: {
                      fontSize: 32,
                    },
                    textAlign: "center"
                  }}
                >
                  Thank you for your donation to {recommendationData.campaign?.name}
                </Typography>

                <Typography
                  sx={{
                    fontSize: 20,
                    fontFamily: "Poppins",
                    fontWeight: 600,
                    lineHeight: 1.5,
                    marginTop: "20px",
                    [theme.breakpoints.down("sm")]: {
                      fontSize: 18,
                    },
                  }}
                >
                  Please select your payment method.
                </Typography>
              </>
            )}
            <Grid container spacing={4}>
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                lg={recommendationData?.campaign ? 9 : 12}
              >
                <Box
                  sx={{
                    background: "#E0E0E0",
                    padding: "0px 20px",
                    height: "80px",
                    margin: "20px 0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                    <Typography
                      fontSize={16}
                      fontWeight={500}
                      fontFamily={"Poppins"}
                      sx={{
                        [theme.breakpoints.down("sm")]: {
                          fontSize: 14,
                        },
                      }}
                    >
                      Amount you selected to invest for impact:
                    </Typography>
                      <TextField
                        type="number"
                        value={investmentAmount}
                        onChange={handleAmountChange}
                        variant="outlined"
                        size="small"
                        sx={{
                          marginLeft: "10px",
                          backgroundColor: "white",
                          borderRadius: "4px",
                        }}
                        onWheel={(e) => (e.target as HTMLInputElement).blur()}
                        inputProps={{
                          style: { textAlign: "start" },
                          sx: {
                            "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button":
                            {
                              "-webkit-appearance": "none",
                              margin: 0,
                            },
                            "&[type=number]": {
                              "-moz-appearance": "textfield",
                            },
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <span style={{ color: "#7A7A7A" }}>$</span>
                            </InputAdornment>
                          ),
                        }}
                      />
                      {investmentError && (
                        <Typography
                          color="error"
                          fontSize={12}
                          sx={{ marginLeft: "10px" }}
                        >
                          {investmentError}
                        </Typography>
                      )}
                  </Box>
                </Box>

                <Typography
                  sx={{
                    fontSize: 24,
                    lineHeight: "30px",
                    textAlign: "left",
                    color: "#000000",
                    fontWeight: 700,
                    fontFamily: "Poppins",
                    [theme.breakpoints.down("sm")]: {
                      fontSize: 22,
                    },
                  }}
                >
                  Contact Information
                </Typography>

                <Grid container pt={3} spacing={8} alignItems="s">
                  <Grid container item spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography
                        fontSize={16}
                        fontWeight={500}
                        fontFamily={"Poppins"}
                        sx={{
                          [theme.breakpoints.down("sm")]: {
                            fontSize: 14,
                          },
                        }}
                      >
                        First Name
                      </Typography>
                      <TextField
                        fullWidth
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        sx={{ "& .MuiInputBase-root": { height: "51px" } }}
                        error={!!formErrors.firstName}
                      />
                      {formErrors.firstName && (
                        <Typography color="error" variant="caption">
                          {formErrors.firstName}
                        </Typography>
                      )}
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Typography
                        fontSize={16}
                        fontWeight={500}
                        fontFamily={"Poppins"}
                        sx={{
                          [theme.breakpoints.down("sm")]: {
                            fontSize: 14,
                          },
                        }}
                      >
                        Last Name
                      </Typography>
                      <TextField
                        fullWidth
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        sx={{ "& .MuiInputBase-root": { height: "51px" } }}
                        error={!!formErrors.lastName}
                      />
                      {formErrors.lastName && (
                        <Typography color="error" variant="caption">
                          {formErrors.lastName}
                        </Typography>
                      )}
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Typography
                        fontSize={16}
                        fontWeight={500}
                        fontFamily={"Poppins"}
                        sx={{
                          [theme.breakpoints.down("sm")]: {
                            fontSize: 14,
                          },
                        }}
                      >
                        Email
                      </Typography>
                      <TextField
                        fullWidth
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        sx={{ "& .MuiInputBase-root": { height: "51px" } }}
                        error={!!formErrors.email}
                      />
                      {formErrors.email && (
                        <Typography color="error" variant="caption">
                          {formErrors.email}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={4} pt={3}>
                  <Typography
                    fontSize={16}
                    fontWeight={500}
                    fontFamily={"Poppins"}
                    sx={{
                      fontSize: 24,
                      lineHeight: "30px",
                      textAlign: "left",
                      color: "#000000",
                      fontWeight: 700,
                      fontFamily: "Poppins",
                      [theme.breakpoints.down("sm")]: {
                        fontSize: 22,
                      },
                    }}
                  >
                    Funding Options
                  </Typography>
                </Grid>

                <Grid container justifyContent="start" pt={3}>
                  <FormControl component="fieldset">
                    <RadioGroup
                      value={paymentMethod}
                      onChange={handlePaymentChange}
                    >
                      <Grid container spacing={2} justifyContent="center">
                        {[
                          {
                            value: "DAF",
                            label: "DAF (Donor Advised Fund)",
                            description:
                              "If you have a Donor Advised Fund (DAF), you can easily grant to fund this investment.",
                          },
                          {
                            value: "ACH",
                            label: "ACH Bank Transfer",
                            description:
                              "Make a donation with ACH bank transfer to fund this investment and receive a tax deductible receipt.",
                          },
                          {
                            value: "Foundation",
                            label: "Foundation Grant",
                            description:
                              "If you have a Foundation, you can easily grant funds to fund your investments.",
                          },
                          {
                            value: "CreditCard",
                            label: "Credit Card",
                            description:
                              "Make a donation with Credit Card to fund this investment and receive a tax deductible receipt.",
                          },
                        ].map((option) => (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            key={option.value}
                            display="flex"
                          >
                            <Paper
                              sx={{
                                padding: "10px",
                                border: `2px solid ${paymentMethod === option.value
                                  ? "#20527F"
                                  : "#E0E0E0"
                                  }`,
                                borderRadius: "10px",
                                width: "100%",
                              }}
                            >
                              <FormControlLabel
                                value={option.value}
                                control={<Radio />}
                                label={
                                  <Box>
                                    <Typography
                                      fontSize={16}
                                      fontWeight={500}
                                      fontFamily={"Poppins"}
                                      sx={{
                                        [theme.breakpoints.down("sm")]: {
                                          fontSize: 14,
                                        },
                                      }}
                                    >
                                      {option.label}
                                    </Typography>
                                    <Typography
                                      fontSize={14}
                                      fontWeight={500}
                                      fontFamily={"Poppins"}
                                      sx={{
                                        [theme.breakpoints.down("sm")]: {
                                          fontSize: 12,
                                        },
                                      }}
                                      color="textSecondary"
                                    >
                                      {option.description}
                                    </Typography>
                                  </Box>
                                }
                                sx={{ width: "100%" }}
                              />
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>

              {recommendationData?.campaign && (
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={12}
                  lg={3}
                  sx={{
                    marginTop: "20px",
                    [theme.breakpoints.up("lg")]: {
                      display: "block",
                    },
                    [theme.breakpoints.down("lg")]: {
                      display: "none",
                    },
                  }}
                >
                  <CampaignCardDetail
                    key={recommendationData?.campaign?.id}
                    campaign={recommendationData?.campaign}
                    investedAmount=""
                    investedDate=""
                    status=""
                  />
                </Grid>
              )}
            </Grid>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                marginTop: "40px",
              }}
            >
              {
                showTerms && <FormControlLabel
                  control={
                    <Checkbox
                      checked={checked}
                      onChange={() => setChecked((pre) => !pre)}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  }
                  label={
                    <Typography
                      fontSize={16}
                      fontWeight={400}
                      fontFamily={"Poppins"}
                      sx={{
                        [theme.breakpoints.down("sm")]: {
                          fontSize: 14,
                        },
                      }}
                    >
                      By donating to CataCap, I agree to the&nbsp;
                      <a href="https://catacap.org/terms-conditions/">
                        Terms and Conditions
                      </a>
                    </Typography>
                  }
                />
              }
              <Button
                variant="contained"
                disabled={disableSubmit}
                onClick={handleSubmit}
                sx={{
                  marginTop: "10px",
                  backgroundColor: "#80B73F",
                  color: "#FFF",
                  padding: "10px 20px",
                  width: "546px",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#9DCF5F" },
                  [theme.breakpoints.down("sm")]: {
                    width: "346px",
                  },
                }}
              >
                Submit
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Snackbar
        open={notificationError.status}
        autoHideDuration={6000}
        onClose={() => setNotificationError({ status: false })}
      >
        <Alert
          onClose={() => setNotificationError({ status: false })}
          severity="error"
          sx={{ width: "100%" }}
        >
          {notificationError.text}
        </Alert>
      </Snackbar>
    </>
  );
};

export default InvestmentPayment;